// src/components/BookingDetailsPage.tsx
import React, { useContext, useState, useRef, useEffect } from 'react'
import { Button, CircularProgress, Grid, Paper, TextField, Typography } from '@material-ui/core'
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
import server, { ApiRequest } from '../api/server'
import ActionCableContext from '../ActionCableContext'

/* ---------- styled helpers ---------------------------------------------- */
const Page = styled.div`
  max-width: 720px;
  margin: 40px auto;
  padding: 0 16px;
`
const FormPaper = styled(Paper)`
  padding: 24px;
  margin-bottom: 32px;
`
const ResponsePaper = styled(Paper)`
  padding: 24px;
  margin-top: 32px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`
const Label = styled(Typography)`
  font-weight: 600;
  color: #212121;
  text-transform: capitalize;
`
const ValueBox = styled.div`
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.5;
  word-break: break-all;
`

const ResultBox = styled.div`
  padding: 8px 12px;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.5;
  word-break: break-all;
`
const RedAsterisk = styled.span`
  color: #f44336;
`

const BlueLabel = styled.span`
  color: #1976d2;
`

const BlackTime = styled.span`
  color: #000;
`
const BlackLine = styled.div`
  border-top: 2px solid #000;
  margin: 16px 0;
  width: 100%;
`
/* ---------- typing ------------------------------------------------------- */
interface ApiOk<T> {
  ok: true
  data: T
}
interface ApiError {
  ok: false
  error: string
}
type ApiResult<T> = ApiOk<T> | ApiError

interface FetchBookingRes {
  result?: Record<string, any>
  status?: string
  priority_list?: []
}
interface TrackShippingRes {
  [k: string]: any
}

/* ---------- helpers ------------------------------------------------------ */
const extractErrorMessage = (raw: unknown): string => {
  if (raw === null || raw === undefined) return 'Error'
  if (typeof raw === 'string') return raw
  if (typeof raw === 'object') {
    // common API pattern: { errors: { detail: 'Not found' } }
    const obj = raw as Record<string, any>
    if (obj.errors) {
      const firstKey = Object.keys(obj.errors)[0]
      if (firstKey) return String(obj.errors[firstKey])
    }
  }
  return 'Error'
}

/* ---------- component ---------------------------------------------------- */
const BookingDetailsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { cable } = useContext(ActionCableContext)
  const stepMessagesActive = useRef(false)
  const subscriptionRef = useRef<any>(null)
  /* form inputs */
  const [bookingNumber, setBookingNumber] = useState('')
  const [userName, setUserName] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [shippingLine, setShippingLine] = useState('')
  const [vesselName, setVesselName] = useState('')

  /* loading + results */
  const [loading, setLoading] = useState(false)
  const [fetchRes, setFetchRes] = useState<ApiResult<FetchBookingRes> | null>(null)
  const [trackRes, setTrackRes] = useState<ApiResult<TrackShippingRes> | null>(null)

  /* ---------------- API calls ------------------------------------------- */

  useEffect(() => {
    const storedToken = sessionStorage.getItem('Access-token')
    const storedUser = sessionStorage.getItem('username')
    if (storedToken) setAccessToken(storedToken)
    if (storedUser) setUserName(storedUser)
  }, [])
  const vesselAPI = (params, showError: boolean) => {
    const req: ApiRequest<TrackShippingRes, typeof params> = {
      method: 'POST',
      path: '/booking_informations/track_shipping_info',
      data: params,
      headers: {
        'Access-Token': accessToken.trim() || '',
        UserName: userName.trim() || '',
      },
      onSuccess: (data) => setTrackRes({ ok: true, data }),
      onFail: (err) => {
        const msg = err?.httpStatus === 404 ? 'Not found' : extractErrorMessage(err.response || err.message)
        if (showError) {
          if (err?.httpStatus !== 404) enqueueSnackbar(`Track shipping: ${msg}`, { variant: 'error' })
          setTrackRes({ ok: false, error: msg })
        }
      },
    }
    server(req)
  }

  /* ---------------- UI helpers ------------------------------------------ */
  const renderKV = (obj: Record<string, any>) => {
    const data = obj.result

    if (Array.isArray(data)) {
      // data is an array of objects
      return data.map((item, index) => (
        <Grid container spacing={2} key={`item-${index}`} style={{ marginBottom: 16 }}>
          {Object.entries(item).map(([k, v]) =>
            k === 'last_updated' ? (
              <span key={k}>
                <RedAsterisk>* </RedAsterisk>
                <BlueLabel>Last updated at: </BlueLabel>
                <BlackTime>{v}</BlackTime>
              </span>
            ) : (
              <Grid item xs={12} sm={6} key={k}>
                <Label variant='subtitle2'>{k.replace(/_/g, ' ')}</Label>
                <ResultBox>{v == null ? '-' : String(v)}</ResultBox>
              </Grid>
            )
          )}
          {index < data.length - 1 && <BlackLine />}
        </Grid>
      ))
    }

    // data is a single object (hash)
    return Object.entries(data).map(([k, v]) => (
      <Grid item xs={12} sm={6} key={k}>
        <Label variant='subtitle2'>{k.replace(/_/g, ' ')}</Label>
        <ResultBox>{v == null ? '-' : String(v)}</ResultBox>
      </Grid>
    ))
  }

  const renderResult = (title: string, result: ApiResult<any> | null) =>
    result && (
      <ResponsePaper>
        <Typography variant='h6' gutterBottom>
          {title}
        </Typography>
        {result.ok ? (
          <Grid container spacing={2}>
            {renderKV(result.data)}
          </Grid>
        ) : (
          <ValueBox style={{ width: '100%', textAlign: 'center' }}>{result.error}</ValueBox>
        )}
      </ResponsePaper>
    )
  const setJobStepMessage = (data: FetchBookingRes) => {
    if (data.priority_list) {
      stepMessages(data.priority_list)
    }
  }

  const stepMessages = (steps: string[]) => {
    let delay = 0
    const allMessages: string[] = []

    steps.forEach((step: string) => {
      allMessages.push('Processing your request...')
      allMessages.push(`Connecting to ${step.toUpperCase()} tracking system...`)
      allMessages.push(`Accessing ${step.toUpperCase()} shipment portal...`)
      allMessages.push(`Submitting your booking number to ${step.toUpperCase()}...`)
      allMessages.push('Checking for CAPTCHA or verification prompts...')
      allMessages.push('Waiting for response...')
      allMessages.push('Received response. Analyzing page content...')
      allMessages.push('Extracting shipment details...')
      allMessages.push('Formatting extracted data...')
      allMessages.push('Validating extracted values...')
      allMessages.push('Finalizing response...')
      allMessages.push('Almost done. Please wait...')
    })

    stepMessagesActive.current = true
    allMessages.forEach((message) => {
      setTimeout(() => {
        if (!stepMessagesActive.current) return
        setFetchRes({ ok: false, error: message })
      }, delay)
      delay += 5000 // Show each message 5s apart
    })
  }

  /* ---------------- submit ---------------------------------------------- */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    setFetchRes(null)
    setTrackRes(null)

    if (!bookingNumber.trim() && !vesselName.trim()) {
      enqueueSnackbar('Fill in Booking Number or Vessel Name before submitting.', { variant: 'warning' })
      return
    }

    setLoading(true)
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }
    sessionStorage.setItem('Access-token', accessToken.trim())
    sessionStorage.setItem('username', userName.trim())
    if (bookingNumber.trim() && cable) {
      subscriptionRef.current = cable.subscriptions.create(
        { channel: 'BookingStatusChannel', booking_number: bookingNumber.trim() },
        {
          received: (data: any) => {
            stepMessagesActive.current = false
            if (data.result) {
              setFetchRes({ ok: true, data })
              if (!vesselName.trim() && data.result.vessel_name) {
                vesselAPI({ vessel_name: data.result.vessel_name }, false)
              }
            } else {
              setFetchRes({ ok: false, error: 'Not found' })
            }
            setLoading(false)
          },
        }
      )
    }
    /* ---- fetch_booking ---- */
    if (bookingNumber.trim()) {
      const params = {
        booking_number: bookingNumber.trim(),
        ...(shippingLine.trim() && { shipping_line: shippingLine.trim() }),
      }

      const req: ApiRequest<FetchBookingRes, typeof params> = {
        method: 'POST',
        path: '/booking_informations/fetch_booking',
        timeout: 200000,
        data: params,
        headers: {
          'Access-Token': accessToken.trim() || '',
          UserName: userName.trim() || '',
        },
        onSuccess: (data) => {
          if (data.result) {
            setFetchRes({ ok: true, data })

            // Auto-trigger vessel tracking if no user input and vessel is available
            const vesselFromFetch = data.result.vessel_name
            if (!vesselName.trim() && vesselFromFetch) {
              vesselAPI({ vessel_name: vesselFromFetch }, false)
            }
            setLoading(false)
          } else {
            setJobStepMessage(data)
          }
        },
        onFail: (err) => {
          const msg = err?.httpStatus === 404 ? 'Not found' : extractErrorMessage(err.response || err.message)
          if (err?.httpStatus !== 404) enqueueSnackbar(`Fetch booking: ${msg}`, { variant: 'error' })
          setFetchRes({ ok: false, error: msg })
          setLoading(false)
        },
      }
      server(req)
    }

    /* ---- track_shipping_info ---- */
    if (vesselName.trim()) {
      const params = { vessel_name: vesselName.trim() }
      vesselAPI(params, true)
      setLoading(false)
    }
  }

  /* ---------------- render ---------------------------------------------- */
  return (
    <Page>
      <Typography variant='h4' style={{ color: 'white' }} gutterBottom>
        Booking Details
      </Typography>

      {/* -------- form -------- */}
      <FormPaper elevation={3}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField label='User Name' fullWidth value={userName} required={true} onChange={(e) => setUserName(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label='Access Token' fullWidth value={accessToken} required={true} onChange={(e) => setAccessToken(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label='Booking Number' fullWidth value={bookingNumber} onChange={(e) => setBookingNumber(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label='Shipping Line' fullWidth value={shippingLine} onChange={(e) => setShippingLine(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label='Vessel Name' fullWidth value={vesselName} onChange={(e) => setVesselName(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <Button type='submit' variant='contained' color='primary' disabled={loading}>
                {loading && <CircularProgress size={20} style={{ marginRight: 8 }} />}
                Send
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormPaper>

      {/* -------- results -------- */}
      {renderResult('Booking Details', fetchRes)}
      {renderResult('Shipping Details', trackRes)}
    </Page>
  )
}

export default BookingDetailsPage
